/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2017-2018, Bulb Inc.  For more information, please email contact@hellobulb.com.
 */

/**
 * organizationGradeFilter directive
 *
 * Renders a small filter icon (▼) next to the Grade column header of a DataTables table.
 * Clicking the icon opens a multi-select popup with grade checkboxes.
 * When the user clicks Apply the directive calls the `onFilterChange` callback with the
 * selected grade array so the parent datatable directive can rebuild its AJAX URL and
 * redraw the table.
 *
 * Usage (added programmatically by organization-datatable-directive):
 *   <organization-grade-filter
 *       table-id="student-table"
 *       on-filter-change="handler(grades)">
 *   </organization-grade-filter>
 */
angular.module('bulbApp.organizationModule')
  .directive('organizationGradeFilter', ['$timeout', '$translate', organizationGradeFilter]);

function organizationGradeFilter($timeout, $translate) {
  return {
    restrict: 'E',
    scope: {
      tableId:        '@',          // e.g. "student-table"
      onFilterChange: '&',          // callback(grades) – called on Apply / Clear
    },
    link: function link(scope, elem) {

      // ── Grade definitions ────────────────────────────────────────────────
      const ALL_GRADES = [
        { value: 'FIRST',        label: '1' },
        { value: 'SECOND',       label: '2' },
        { value: 'THIRD',        label: '3' },
        { value: 'FOURTH',       label: '4' },
        { value: 'FIFTH',        label: '5' },
        { value: 'SIXTH',        label: '6' },
        { value: 'SEVENTH',      label: '7' },
        { value: 'EIGHTH',       label: '8' },
        { value: 'NINTH',        label: '9' },
        { value: 'TENTH',        label: '10' },
        { value: 'ELEVENTH',     label: '11' },
        { value: 'TWELFTH',      label: '12' },
        { value: 'POSTGRADUATE', label: $translate.instant('grade_level_POSTGRADUATE') || 'Postgraduate' },
      ];

      // Active filter (committed by Apply). Empty means no filter — show all grades.
      let selectedGrades = [];
      // Draft selection while popup is open (committed on Apply, discarded on close-outside)
      let pendingGrades  = [];

      const POPUP_ID = `grade-filter-popup-${scope.tableId}`;

      // Returns true when a real partial filter is active (not all-or-none).
      function isFilterActive() {
        return selectedGrades.length > 0 && selectedGrades.length < ALL_GRADES.length;
      }

      // ── Icon injection ───────────────────────────────────────────────────

      /**
       * Appends the filter icon <i> to the Grade column header (3rd <th>).
       * Safe to call multiple times – skips if the icon is already present.
       */
      function injectIcon() {
        const $th = $(`#${scope.tableId} thead tr th:nth-child(3)`);
        if (!$th.length || $th.find('.grade-filter-icon').length) return;

        const color    = isFilterActive() ? '#4a90d9' : '#999';
        const $icon    = $(`<i class="grade-filter-icon far fa-filter"
          title="Filter by grade"
          style="cursor:pointer;margin-left:6px;font-size:11px;color:${color};vertical-align:middle;"></i>`);

        $th.append($icon);

        $icon.on('click', function (e) {
          e.stopPropagation();
          openPopup($icon);
        });
      }

      /**
       * Updates the icon colour to reflect whether a filter is currently active.
       */
      function refreshIconState() {
        $(`#${scope.tableId} thead tr th:nth-child(3) .grade-filter-icon`)
          .css('color', isFilterActive() ? '#4a90d9' : '#999');
      }

      // ── Popup ────────────────────────────────────────────────────────────

      // Returns inline style for the custom checkbox box span
      function boxStyle(isChecked) {
        return isChecked
          ? 'display:inline-block;width:14px;height:14px;border:2px solid #4a90d9;background:#4a90d9;border-radius:2px;position:relative;'
          : 'display:inline-block;width:14px;height:14px;border:2px solid #aaa;background:#fff;border-radius:2px;position:relative;';
      }

      // Checkmark svg drawn inside the box when checked
      const CHECKMARK = `<svg viewBox="0 0 10 8" style="position:absolute;top:1px;left:1px;width:10px;height:8px;" xmlns="http://www.w3.org/2000/svg">
        <polyline points="1,4 4,7 9,1" style="fill:none;stroke:#fff;stroke-width:1.8;stroke-linecap:round;stroke-linejoin:round;"/>
      </svg>`;

      function buildSelectAllRow() {
        const allChecked = ALL_GRADES.every((g) => pendingGrades.indexOf(g.value) !== -1);
        return `<div data-select-all-row
            style="display:table;width:100%;cursor:pointer;border-bottom:1px solid #e8e8e8;background:#fff;box-sizing:border-box;">
            <input type="checkbox" data-select-all ${allChecked ? 'checked' : ''}
              style="display:none;" />
            <div style="display:table-cell;width:34px;text-align:center;vertical-align:middle;padding:6px 0;">
              <span data-box style="${boxStyle(allChecked)}">${allChecked ? CHECKMARK : ''}</span>
            </div>
            <div style="display:table-cell;vertical-align:middle;padding:6px 10px 6px 0;font-size:13px;font-weight:600;font-family:inherit;color:#333;line-height:1.4;cursor:pointer;">
              Select All
            </div>
          </div>`;
      }

      function buildCheckboxRows() {
        const gradeRows = ALL_GRADES.map((g) => {
          const isChecked = pendingGrades.indexOf(g.value) !== -1;
          const checked   = isChecked ? 'checked' : '';
          /*
           * Layout strategy: display:table on the row with two display:table-cell children.
           * Left cell is fixed 34px wide and holds the custom checkbox box.
           * Right cell holds the label text and fills remaining width.
           * display:table-cell is immune to Foundation overrides and guarantees
           * both cells are always on the same row, vertically centred.
           */
          return `<div data-grade-row
              style="display:table;width:100%;cursor:pointer;background:#fff;box-sizing:border-box;">
              <input type="checkbox" value="${g.value}" ${checked}
                style="display:none;" />
              <div style="display:table-cell;width:34px;text-align:center;vertical-align:middle;padding:6px 0;">
                <span data-box style="${boxStyle(isChecked)}">${isChecked ? CHECKMARK : ''}</span>
              </div>
              <div style="display:table-cell;vertical-align:middle;padding:6px 10px 6px 0;font-size:13px;font-weight:normal;font-family:inherit;color:#333;line-height:1.4;cursor:pointer;">
                ${g.label}
              </div>
            </div>`;
        }).join('');

        return buildSelectAllRow() + gradeRows;
      }

      function openPopup($icon) {
        closePopup();

        // If no partial filter is active, default to all grades selected in the popup
        pendingGrades = selectedGrades.length > 0 ? selectedGrades.slice() : ALL_GRADES.map((g) => g.value);

        const $popup = $(`
          <div id="${POPUP_ID}"
            style="position:absolute;z-index:9999;background:#fff;
                   border:1px solid #d0d0d0;border-radius:4px;
                   box-shadow:0 4px 12px rgba(0,0,0,0.18);
                   min-width:130px;max-width:160px;overflow:hidden;">

            <div style="max-height:220px;overflow-y:auto;padding:4px 0;">
              ${buildCheckboxRows()}
            </div>

            <div style="border-top:1px solid #e8e8e8;display:table;width:100%;table-layout:fixed;">
              <div style="display:table-cell;width:50%;padding:8px 4px 8px 8px;">
                <button class="gf-btn-clear"
                  style="display:block;width:100%;padding:5px 0;
                         font-size:12px;font-family:inherit;cursor:pointer;
                         background:#fff;border:1px solid #bbb;border-radius:3px;
                         color:#555;text-align:center;box-sizing:border-box;">
                  <i class="far fa-times" style="margin-right:3px;"></i>Clear
                </button>
              </div>
              <div style="display:table-cell;width:50%;padding:8px 8px 8px 4px;">
                <button class="gf-btn-apply"
                  style="display:block;width:100%;padding:5px 0;
                         font-size:12px;font-family:inherit;cursor:pointer;
                         background:#4a90d9;border:1px solid #3a7bbf;border-radius:3px;
                         color:#fff;text-align:center;box-sizing:border-box;">
                  <i class="far fa-check" style="margin-right:3px;"></i>Apply
                </button>
              </div>
            </div>
          </div>`);

        // Position below the icon
        const offset = $icon.offset();
        $popup.css({ top: offset.top + $icon.outerHeight() + 4, left: offset.left });
        $('body').append($popup);

        // ── Event handlers inside the popup ─────────────────────────────

        // Stop all clicks inside from bubbling to the document close-handler
        $popup.on('click', (e) => e.stopPropagation());

        // Update only the custom checkbox box to reflect checked state — row background unchanged
        function syncRowHighlight() {
          $popup.find('[data-grade-row]').each(function () {
            const isChecked = $(this).find('input[type="checkbox"]').is(':checked');
            const $box = $(this).find('[data-box]');
            $box.attr('style', boxStyle(isChecked));
            $box.html(isChecked ? CHECKMARK : '');
          });
          // Select All box
          const allChecked = ALL_GRADES.every((g) => pendingGrades.indexOf(g.value) !== -1);
          const $saBox = $popup.find('[data-select-all-row] [data-box]');
          $saBox.attr('style', boxStyle(allChecked));
          $saBox.html(allChecked ? CHECKMARK : '');
        }

        // Sync the Select All checkbox state based on individual checkboxes
        function syncSelectAll() {
          const total   = ALL_GRADES.length;
          const checked = $popup.find('input[type="checkbox"][value]').filter(':checked').length;
          const $sa     = $popup.find('input[data-select-all]');
          $sa.prop('checked', checked === total);
          // Use indeterminate state when some (but not all) are selected
          $sa.prop('indeterminate', checked > 0 && checked < total);
        }

        // Clicking anywhere on the Select All row toggles all grades
        $popup.find('[data-select-all-row]').on('click', function () {
          const $sa = $(this).find('input[data-select-all]');
          $sa.prop('checked', !$sa.prop('checked')).trigger('change');
        });

        // Select All checkbox change — check or uncheck every grade row
        $popup.find('input[data-select-all]').on('change', function () {
          const nowChecked = $(this).is(':checked');
          if (nowChecked) {
            pendingGrades = ALL_GRADES.map((g) => g.value);
          } else {
            pendingGrades = [];
          }
          $popup.find('input[type="checkbox"][value]').prop('checked', nowChecked);
          $(this).prop('indeterminate', false);
          syncRowHighlight();
        });

        // Clicking anywhere on a grade row toggles its checkbox
        $popup.find('[data-grade-row]').on('click', function () {
          const $cb = $(this).find('input[type="checkbox"]');
          $cb.prop('checked', !$cb.prop('checked')).trigger('change');
        });

        // Track individual checkbox state in pendingGrades and keep Select All in sync
        $popup.find('input[type="checkbox"][value]').on('change', function () {
          const val = $(this).val();
          if ($(this).is(':checked')) {
            if (pendingGrades.indexOf(val) === -1) pendingGrades.push(val);
          } else {
            pendingGrades = pendingGrades.filter((g) => g !== val);
          }
          syncSelectAll();
          syncRowHighlight();
        });

        // Apply – commit pending selection and notify parent.
        // If all grades are selected, treat it as no filter (pass empty array).
        $popup.find('.gf-btn-apply').on('click', function () {
          selectedGrades = pendingGrades.length === ALL_GRADES.length ? [] : pendingGrades.slice();
          closePopup();
          refreshIconState();
          scope.onFilterChange({ grades: selectedGrades });
        });

        // Clear – uncheck all grades inside the popup without closing it.
        // The user must click Apply (or outside) to dismiss.
        $popup.find('.gf-btn-clear').on('click', function () {
          pendingGrades = [];
          $popup.find('input[type="checkbox"][value]').prop('checked', false);
          syncSelectAll();
          syncRowHighlight();
        });

        // Close when clicking anywhere outside the popup
        $timeout(() => {
          $(document).one(`click.gradeFilter.${scope.tableId}`, () => closePopup());
        }, 0);
      }

      function closePopup() {
        $(`#${POPUP_ID}`).remove();
        $(document).off(`click.gradeFilter.${scope.tableId}`);
      }

      // ── Public API exposed on the element for the parent directive ───────
      // The datatable directive calls these after each draw.
      elem[0].injectIcon  = injectIcon;

      // ── Cleanup ──────────────────────────────────────────────────────────
      scope.$on('$destroy', () => {
        closePopup();
        $(`#${scope.tableId} thead tr th:nth-child(3) .grade-filter-icon`).off('click');
      });
    },
  };
}

